import { useEffect, useState, useCallback } from 'react';
import {
  MessageSquare,
  Search,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { apiFetch } from '../lib/api';

// ============================================================================
// Types
// ============================================================================

interface MessageLog {
  id: number;
  source: string;
  templateKey: string | null;
  taskId: number | null;
  recipientPhone: string;
  recipientName: string | null;
  messageContent: string;
  status: 'sent' | 'failed' | 'pending';
  errorMessage: string | null;
  sentAt: string | null;
  createdAt: string;
}

interface MessageLogsResponse {
  logs: MessageLog[];
  total: number;
}

// ============================================================================
// Constants
// ============================================================================

const LIMIT = 20;

const SOURCE_OPTIONS = [
  { value: '', label: '全部來源' },
  { value: 'system_auto', label: '系統自動' },
  { value: 'broadcast', label: '廣播' },
  { value: 'manual', label: '手動' },
];

const STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'sent', label: '已發送' },
  { value: 'failed', label: '失敗' },
  { value: 'pending', label: '待發送' },
];

const STATUS_COLORS: Record<string, string> = {
  sent: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
};

const STATUS_LABELS: Record<string, string> = {
  sent: '已發送',
  failed: '失敗',
  pending: '待發送',
};

const SOURCE_LABELS: Record<string, string> = {
  system_auto: '系統自動',
  broadcast: '廣播',
  manual: '手動',
};

// ============================================================================
// Helpers
// ============================================================================

function isToday(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

// ============================================================================
// Component
// ============================================================================

export default function AdminMessageLogs() {
  const [logs, setLogs] = useState<MessageLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams();
    query.set('limit', String(LIMIT));
    query.set('offset', String(offset));
    if (sourceFilter) query.set('source', sourceFilter);
    if (statusFilter) query.set('status', statusFilter);
    if (phoneFilter) query.set('phone', phoneFilter);

    const res = await apiFetch<MessageLogsResponse>(
      `/api/admin/message-logs?${query.toString()}`
    );
    if (res.success && res.data) {
      setLogs(res.data.logs);
      setTotal(res.data.total);
    }
    setLoading(false);
  }, [offset, sourceFilter, statusFilter, phoneFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Stats computed from currently visible data
  const sentCount = logs.filter((l) => l.status === 'sent').length;
  const failedCount = logs.filter((l) => l.status === 'failed').length;
  const todayCount = logs.filter((l) => isToday(l.createdAt)).length;

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const currentPage = Math.floor(offset / LIMIT) + 1;

  const formatDateTime = (str: string) =>
    new Date(str).toLocaleString('zh-HK', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare className="w-6 h-6 text-brand-600" />
        <h1 className="text-2xl font-bold text-gray-900">訊息發送記錄</h1>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Send className="w-4 h-4 text-brand-600" />
            <span className="text-sm text-gray-500">總發送數</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{total}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="text-sm text-gray-500">成功數</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{sentCount}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-gray-500">失敗數</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{failedCount}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-gray-500">今日發送數</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{todayCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setOffset(0);
            }}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setOffset(0);
            }}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜尋電話號碼"
              value={phoneFilter}
              onChange={(e) => {
                setPhoneFilter(e.target.value);
                setOffset(0);
              }}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
            />
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                  時間
                </th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                  來源
                </th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                  模板
                </th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                  收件人
                </th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                  電話
                </th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                  狀態
                </th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">
                  內容預覽
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {formatDateTime(log.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {SOURCE_LABELS[log.source] || log.source}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {log.templateKey || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {log.recipientName || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap font-mono text-xs">
                      {log.recipientPhone}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[log.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                      >
                        {STATUS_LABELS[log.status] || log.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs">
                      <button
                        onClick={() => toggleExpand(log.id)}
                        className="text-left w-full hover:text-brand-600 transition-colors"
                      >
                        {expandedIds.has(log.id)
                          ? log.messageContent
                          : log.messageContent.length > 50
                            ? log.messageContent.slice(0, 50) + '...'
                            : log.messageContent}
                      </button>
                      {log.errorMessage && (
                        <p className="text-xs text-red-500 mt-1">
                          {log.errorMessage}
                        </p>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    沒有符合條件的記錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4 animate-pulse"
            >
              <div className="h-4 bg-gray-100 rounded w-1/3 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-full mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
            </div>
          ))
        ) : logs.length > 0 ? (
          logs.map((log) => (
            <div
              key={log.id}
              className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4 space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {formatDateTime(log.createdAt)}
                </span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[log.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                >
                  {STATUS_LABELS[log.status] || log.status}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">來源</span>
                <span className="text-gray-700">
                  {SOURCE_LABELS[log.source] || log.source}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">模板</span>
                <span className="text-gray-500">
                  {log.templateKey || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">收件人</span>
                <span className="text-gray-700">
                  {log.recipientName || '—'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">電話</span>
                <span className="text-gray-500 font-mono text-xs">
                  {log.recipientPhone}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-gray-500 block mb-1">內容</span>
                <button
                  onClick={() => toggleExpand(log.id)}
                  className="text-left text-gray-700 w-full hover:text-brand-600 transition-colors"
                >
                  {expandedIds.has(log.id)
                    ? log.messageContent
                    : log.messageContent.length > 50
                      ? log.messageContent.slice(0, 50) + '...'
                      : log.messageContent}
                </button>
                {log.errorMessage && (
                  <p className="text-xs text-red-500 mt-1">
                    {log.errorMessage}
                  </p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-16 text-gray-400">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>沒有符合條件的記錄</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            disabled={offset === 0}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 px-3">
            第 {currentPage} / {totalPages} 頁
          </span>
          <button
            onClick={() =>
              setOffset((o) =>
                Math.min((totalPages - 1) * LIMIT, o + LIMIT)
              )
            }
            disabled={currentPage >= totalPages}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
